import { Injectable, NgZone, computed, inject, signal, OnDestroy, effect, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppConfig } from '@env/app-config';
import { ApiResponse } from '@models/common/api-response.model';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { NotificationService } from '@shared/services/notificacion/notification.service';

export interface ActiveTimerDTO {
  id: number;
  taskId: number | null;
  /** Código legible de la tarea (ej. "NEXUS-142"); null si el timer no tiene tarea. */
  taskCode: string | null;
  /** Título de la tarea; null si el timer no tiene tarea. */
  taskTitle: string | null;
  description: string | null;
  /**
   * RF-002: el timer activo es uno por usuario en TODO el sistema, no por empresa — puede
   * pertenecer a una empresa distinta de la que el usuario tiene abierta ahora mismo.
   */
  companyId: number;
  companyName: string | null;
  startedAt: string;
}

/** El timer que `start()` acaba de cerrar al abrir uno nuevo — para avisar "se detuvo X". */
export interface StoppedTimerNotice {
  id: number;
  taskCode: string | null;
  taskTitle: string | null;
  companyId: number;
  companyName: string | null;
}

interface TimerBroadcastMessage {
  type: 'timer-state';
  timer: ActiveTimerDTO | null;
}

/**
 * Fuente única de verdad del temporizador activo del usuario — consumida por el widget
 * persistente del header, el botón del panel de tarea y la pestaña "Registro de Tiempo" de M07,
 * para que los tres siempre muestren el mismo estado.
 *
 * <p>Incidente de concurrencia (RF-002): antes solo se refrescaba al cargar, cambiar de empresa,
 * o en el start/stop de ESA pestaña — dos pestañas o dispositivos se desincronizaban ("no hay
 * timer" en una mientras el backend sí tiene uno abierto por la otra). Ahora se mantiene al día
 * por tres vías, en orden de preferencia (más barata primero):
 * <ol>
 *   <li>{@link BroadcastChannel}: la pestaña que cambia el estado lo publica; las demás lo
 *       aplican sin volver a pedir nada al servidor.</li>
 *   <li>Refresco al recuperar visibilidad/foco (con un debounce corto): cubre pestañas que no
 *       comparten origen de BroadcastChannel, o donde no está disponible.</li>
 *   <li>Poll de respaldo cada 60s, solo mientras haya un timer activo y la pestaña esté visible:
 *       red de seguridad última, no el mecanismo principal.</li>
 * </ol>
 */
@Injectable({ providedIn: 'root' })
export class GlobalTimerService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly companyContext = inject(CompanyContextService);
  private readonly notification = inject(NotificationService);
  private readonly zone = inject(NgZone);
  private readonly apiUrl = `${AppConfig.apiUrl}/api/nexus/time-entries`;

  private static readonly CHANNEL_NAME = 'smartti-timer';
  private static readonly VISIBILITY_DEBOUNCE_MS = 400;
  private static readonly POLL_MS = 60_000;

  readonly activeTimer = signal<ActiveTimerDTO | null>(null);
  readonly elapsedSecs = signal(0);

  /**
   * Contador que sube cada vez que `activeTimer` se reasigna (al cargar, cambiar de empresa,
   * iniciar, detener, o al recibir el estado de otra pestaña). La pestaña "Tiempo" del panel de
   * tarea lo observa para saber cuándo recargar su resumen, sin necesitar su propio pegamento con
   * start()/stop().
   */
  readonly changes = signal(0);

  /**
   * El timer que `start()` acaba de cerrar al abrir uno nuevo, o `null` cuando no hay ningún
   * aviso pendiente. Quien lo muestre debe llamar {@link clearStoppedNotice} después, para no
   * repetirlo en el siguiente cambio de estado.
   */
  readonly stoppedNotice = signal<StoppedTimerNotice | null>(null);

  readonly elapsedLabel = computed(() => {
    const total = this.elapsedSecs();
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
  });

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private pollId: ReturnType<typeof setInterval> | null = null;
  private visibilityDebounceId: ReturnType<typeof setTimeout> | null = null;
  private channel: BroadcastChannel | null = null;

  constructor() {
    effect(() => {
      const companyId = this.companyContext.companyId();
      if (companyId) untracked(() => this.refresh());
    });

    this.setupBroadcastChannel();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('focus', this.onFocus);
    }
  }

  ngOnDestroy(): void {
    this.clearTick();
    this.clearPoll();
    if (this.visibilityDebounceId !== null) clearTimeout(this.visibilityDebounceId);
    this.channel?.close();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('focus', this.onFocus);
    }
  }

  refresh(): void {
    this.http.get<ActiveTimerDTO | null>(`${this.apiUrl}/active-timer`).subscribe({
      next: (timer) => this.applyTimer(timer, true),
      error: () => this.applyTimer(null, true),
    });
  }

  start(taskId?: number, description?: string): void {
    const companyId = this.companyContext.companyId();
    if (!companyId) return;
    this.http.post<ApiResponse>(`${this.apiUrl}/start`, { companyId, taskId, description }).subscribe({
      next: (res) => {
        const stopped = (res.data as Record<string, unknown> | undefined)?.['stoppedEntry'] as StoppedTimerNotice | undefined;
        if (stopped) this.stoppedNotice.set(stopped);
        this.refresh();
      },
      // Aunque falle, puede que el backend sí haya cambiado algo (p. ej. detuvo el anterior antes
      // de que la escritura del nuevo chocara) — refrescar deja ver el estado real, no el que esta
      // pestaña asumía.
      error: () => this.refresh(),
    });
  }

  stop(): void {
    const timer = this.activeTimer();
    if (!timer) return;
    this.http.put<ApiResponse>(`${this.apiUrl}/stop/${timer.id}`, {}).subscribe({
      next: (response) => {
        const data = response.data as { belowMinimum?: boolean; minimumEntryMinutes?: number } | undefined;
        if (data?.belowMinimum) {
          this.notification.showWarning(`El tiempo se registró, pero quedó por debajo del mínimo configurado (${data.minimumEntryMinutes} min).`);
        }
        this.applyTimer(null, true);
      },
      error: () => this.refresh(),
    });
  }

  clearStoppedNotice(): void {
    this.stoppedNotice.set(null);
  }

  private applyTimer(timer: ActiveTimerDTO | null, publish: boolean): void {
    this.activeTimer.set(timer);
    this.changes.update(n => n + 1);
    this.clearTick();
    this.updatePoll();
    if (publish) this.publishToChannel(timer);

    if (!timer) { this.elapsedSecs.set(0); return; }

    const tick = () => {
      const secs = Math.max(0, Math.floor((Date.now() - new Date(timer.startedAt.replace(' ', 'T')).getTime()) / 1000));
      this.elapsedSecs.set(secs);
    };
    tick();
    this.zone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.zone.run(tick), 1000);
    });
  }

  private clearTick(): void {
    if (this.intervalId !== null) { clearInterval(this.intervalId); this.intervalId = null; }
  }

  // ── BroadcastChannel: sincroniza pestañas del mismo origen sin volver a pedir nada ──────────

  private setupBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') return; // navegador sin soporte: fallback silencioso
    try {
      this.channel = new BroadcastChannel(GlobalTimerService.CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<TimerBroadcastMessage>) => {
        if (event.data?.type !== 'timer-state') return;
        // No se republica lo que llega de fuera: evita el eco entre pestañas.
        this.zone.run(() => this.applyTimer(event.data.timer, false));
      };
    } catch {
      this.channel = null;
    }
  }

  private publishToChannel(timer: ActiveTimerDTO | null): void {
    if (!this.channel) return;
    try {
      this.channel.postMessage({ type: 'timer-state', timer } as TimerBroadcastMessage);
    } catch {
      // Un fallo al publicar (canal cerrado, etc.) no debe romper el flujo local.
    }
  }

  // ── Refresco al recuperar visibilidad/foco, con un debounce corto ───────────────────────────

  private readonly onFocus = (): void => {
    this.debouncedRefresh();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.debouncedRefresh();
      this.updatePoll(); // re-arma el poll si hay timer activo; lo habíamos parado al ocultar
    } else {
      this.clearPoll();
    }
  };

  private debouncedRefresh(): void {
    if (this.visibilityDebounceId !== null) clearTimeout(this.visibilityDebounceId);
    this.zone.runOutsideAngular(() => {
      this.visibilityDebounceId = setTimeout(() => this.zone.run(() => this.refresh()), GlobalTimerService.VISIBILITY_DEBOUNCE_MS);
    });
  }

  // ── Poll de respaldo: cada 60s, solo con timer activo y pestaña visible ─────────────────────

  private updatePoll(): void {
    this.clearPoll();
    if (!this.activeTimer()) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    this.zone.runOutsideAngular(() => {
      this.pollId = setInterval(() => this.zone.run(() => this.refresh()), GlobalTimerService.POLL_MS);
    });
  }

  private clearPoll(): void {
    if (this.pollId !== null) { clearInterval(this.pollId); this.pollId = null; }
  }
}
